import { useSyncExternalStore } from "react";

/**
 * The clock a contest time is shown on (`EX-PUB18`, founder rule 2026-09-23,
 * `docs/04` "Timezone and formatting"): a visitor in Bangladesh sees
 * Bangladesh time, everyone else sees UTC, and the zone is printed beside the
 * time either way.
 *
 * "In Bangladesh" is read from the device's own time zone, not from the
 * request's IP. The public contest pages are ISR-cached, so the server cannot
 * answer per visitor, and a visitor on a VPN is still wherever their clock
 * says they are.
 */
export type ContestDisplayZone = "Asia/Dhaka" | "UTC";

/** `Asia/Dacca` is the pre-2010 IANA spelling some older systems still report. */
const BANGLADESH_ZONES: ReadonlySet<string> = new Set(["Asia/Dhaka", "Asia/Dacca"]);

export function contestDisplayZoneFor(deviceZone: string | null | undefined): ContestDisplayZone {
  return deviceZone && BANGLADESH_ZONES.has(deviceZone) ? "Asia/Dhaka" : "UTC";
}

function deviceTimeZone(): string | undefined {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone;
  } catch {
    return undefined;
  }
}

/** A device's zone does not change under a page, so there is nothing to subscribe to. */
function subscribe(): () => void {
  return () => {};
}

function clientZone(): ContestDisplayZone {
  return contestDisplayZoneFor(deviceTimeZone());
}

/**
 * The server renders Bangladesh time, the platform's home audience, and so does
 * the hydrating render; a visitor elsewhere re-renders to UTC straight after.
 * Reading the device during the first render instead would make the server's
 * HTML and the browser's disagree for every visitor outside Bangladesh.
 */
function serverZone(): ContestDisplayZone {
  return "Asia/Dhaka";
}

export function useContestDisplayZone(): ContestDisplayZone {
  return useSyncExternalStore(subscribe, clientZone, serverZone);
}
